'use client';
import React from 'react';

export default function Footer() {
  const currentYear = 2025;

  const services = [
    'Veneers',
    'Smile Design',
    'Full Mouth Rehabilitation',
    'Full Mouth Implant Restoration',
    'Cosmetic Dentistry',
    'Dental Consultation',
  ];

  const quickLinks = [
    { label: 'About Dr. Pal', href: '#about' },
    { label: 'Our Services', href: '#services' },
    { label: 'Doctor Profile', href: '#doctor' },
    { label: 'Patient Reviews', href: '#testimonials' },
    { label: 'Book Appointment', href: '#contact' },
  ];

  const handleNavClick = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <footer className="bg-dark text-white">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-20">
        <div className="grid lg:grid-cols-4 gap-10 lg:gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-2.5 mb-5">
              <div className="w-9 h-9 bg-primary rounded-xl flex items-center justify-center flex-shrink-0">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 2C8.5 2 6 4.5 6 7c0 1.5.5 2.8 1.3 3.8L6 20c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2l-1.3-9.2C17.5 9.8 18 8.5 18 7c0-2.5-2.5-5-6-5z"
                    fill="#131c15"
                  />
                </svg>
              </div>
              <div>
                <span className="font-unbounded font-bold text-white text-sm tracking-tight block leading-tight">
                  DrPal's Dentistry
                </span>
                <span className="font-inter text-[10px] text-white/40">drpals.in</span>
              </div>
            </div>
            <p className="font-inter text-white/45 text-sm leading-relaxed mb-6">
              Expert prosthodontic and implant care by Dr. Abhishek Pal. Transforming smiles with precision,
              artistry, and compassion.
            </p>
            <div className="flex gap-2.5">
              {[
                {
                  label: 'Instagram',
                  icon: (
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                    </svg>
                  ),
                },
                {
                  label: 'Facebook',
                  icon: (
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                    </svg>
                  ),
                },
              ].map((social) => (
                <a
                  key={social.label}
                  href="#"
                  aria-label={social.label}
                  className="w-9 h-9 bg-white/8 border border-white/10 rounded-xl flex items-center justify-center text-white/50 hover:bg-primary hover:text-dark hover:border-primary transition-all duration-200"
                >
                  {social.icon}
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-unbounded font-bold text-white text-xs mb-5 uppercase tracking-wider">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.label}>
                  <button
                    onClick={() => handleNavClick(link.href)}
                    className="font-inter text-sm text-white/45 hover:text-white transition-colors duration-200 text-left hover:translate-x-1 inline-flex items-center gap-1.5 group"
                  >
                    <span className="w-0 group-hover:w-3 h-px bg-primary transition-all duration-200 overflow-hidden" />
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-unbounded font-bold text-white text-xs mb-5 uppercase tracking-wider">Services</h4>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service}>
                  <button
                    onClick={() => handleNavClick('#services')}
                    className="font-inter text-sm text-white/45 hover:text-white transition-colors duration-200 text-left hover:translate-x-1 inline-flex items-center gap-1.5 group"
                  >
                    <span className="w-0 group-hover:w-3 h-px bg-primary transition-all duration-200 overflow-hidden" />
                    {service}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-unbounded font-bold text-white text-xs mb-5 uppercase tracking-wider">Contact</h4>
            <div className="space-y-4">
              <div>
                <p className="font-inter text-xs text-white/30 mb-1">Website</p>
                <p className="font-inter text-sm text-white/65">www.DrPals.in</p>
              </div>
              <div>
                <p className="font-inter text-xs text-white/30 mb-1">Email</p>
                <p className="font-inter text-sm text-white/65">info@drpals.in</p>
              </div>
              <div>
                <p className="font-inter text-xs text-white/30 mb-1">Hours</p>
                <p className="font-inter text-sm text-white/65">Mon–Fri: 9AM–8PM</p>
                <p className="font-inter text-sm text-white/65">Sat: 10AM–6PM</p>
              </div>
              <button
                onClick={() => handleNavClick('#contact')}
                className="mt-2 bg-primary text-dark font-inter font-semibold text-sm px-6 py-3 rounded-full hover:bg-primary-dark transition-all duration-200 hover:shadow-teal"
              >
                Book Appointment
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/8">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="font-inter text-xs text-white/30">
            © {currentYear} DrPal's Dentistry. All rights reserved.
          </p>
          <p className="font-inter text-xs text-white/30">
            Dr. Abhishek Pal — Prosthodontist & Implantologist
          </p>
        </div>
      </div>
    </footer>
  );
}
